import React, { useEffect, useRef } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikGrid from '../../../common/formik/FormikGrid';
import FormikButton from '../../../common/formik/FormikButton';
import FormikTextField from '../../../common/formik/FormikTextField';
import TaskTypeEntitySelector from '../components/TaskTypeEntitySelector';
import { TaskType } from '../TaskType.interface';
import { useCreateTaskType } from '../TaskType.hooks';

export interface TaskTypeCreateDialogProps {
  open: boolean;
  onCancel?: () => void;
  onCreated?: (taskType: TaskType) => void;
}

const TaskTypeCreateDialog: React.FC<TaskTypeCreateDialogProps> = (
  props: TaskTypeCreateDialogProps
) => {
  const { open, onCancel, onCreated } = props;
  const formikRef = useRef<FormikProps<Partial<TaskType>> | undefined>();
  const [{ data, isLoading, error }, createTaskType] = useCreateTaskType();

  useEffect(() => {
    formikRef.current?.setSubmitting(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (data) {
      onCreated?.(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog maxWidth='xs' open={open} onClose={onCancel}>
      <Formik
        innerRef={(formik: any) => (formikRef.current = formik)}
        initialValues={{ name: '', label: '', prefix: '' }}
        validationSchema={Yup.object({
          name: Yup.string().required('Required'),
          label: Yup.string().required('Required'),
          prefix: Yup.string().required('Required'),
          targetEntity: Yup.string().required('Required')
        })}
        onSubmit={(form: Partial<TaskType>) => createTaskType(form)}
      >
        {({ values, errors, setFieldValue }) => (
          <Form autoComplete='off'>
            <DialogTitle>Create Task Type</DialogTitle>
            <DialogContent>
              <FormikGrid>
                <FormikTextField
                  autoFocus
                  type='text'
                  name='name'
                  label='Name'
                  placeholder='Enter the Task Type name'
                  disabled={isLoading}
                />
                <FormikTextField
                  type='text'
                  name='label'
                  label='Label'
                  placeholder='Enter the Task Type label'
                  disabled={isLoading}
                />
                <FormikTextField
                  type='text'
                  name='prefix'
                  label='Prefix'
                  placeholder='Enter the Task Type prefix'
                  disabled={isLoading}
                />
                <Grid item xs={12}>
                  <TaskTypeEntitySelector
                    disabled={isLoading}
                    value={values.targetEntity}
                    errorMessage={errors.targetEntity}
                    onChange={(entity: string | null) => {
                      setFieldValue('targetEntity', entity || undefined, true);
                    }}
                  />
                </Grid>
              </FormikGrid>
              {error && <Alert severity='error'>{error?.message}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <FormikButton>Create</FormikButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TaskTypeCreateDialog;
